/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ttsService, {
  IBook, IProject, GTSVoice, LanguageOption, IClip, IMergedChapter,
  CORE_LANGUAGE_OPTIONS,
  mergeLanguageOptions,
  OFFLINE_VOICE_LANGUAGE_CODES,
  STANDARD_FALLBACK_LANGUAGE_OPTIONS,
} from "../../services/ttsService";
import { mergeAudioClips } from "../../utils/audioUtils";
import { BookConfig, Clip, ChapterOrderEntry, getChapterSerial, orderedChapterNames, mergeAdvanced, MergedChapterUI, sortClipsByCreatedAt, sortClipsForDisplay } from "../../utils/types";
import FormField from "../../components/FormField";
import { ALL_MODELS, AUDIO_ENCODINGS, EFFECTS_PROFILES } from "../../utils/constants";
import ConfirmModal from "../../components/ConfirmModal";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import AudioPlayer from "../../components/MediaPlayer/MediaPlayer";
import { toast } from "react-toastify";

interface Props {
  project: IProject;
  book: IBook;
  onBack: () => void;
  onUpdate: (b: IBook) => void;
}

function downloadBlob(blobUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = blobUrl; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}
function safe(name: string) {
  return name.replace(/[^a-zA-Z0-9\-_ ]/g, "_").trim();
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}
const CLOSED_CONFIRM: ConfirmState = {
  open: false, title: "", message: "", confirmLabel: "Delete", onConfirm: () => { },
};

const BookEditor: React.FC<Props> = ({ project, book, onBack, onUpdate }) => {

  const [bookCfg, setBookCfg] = useState<BookConfig>({
    title: book.title,
    author: book.author || "",
    language: book.language,
    model: book.model,
    platform: book.platform === "Google Cloud TTS" ? "Vertex AI" : (book.platform || "Vertex AI"),
    realVoice: book.realVoice,
    voiceLabel: book.voiceLabel || "",
    styleInstructions: book.styleInstructions || "",
    notes: book.notes || "",
    advanced: mergeAdvanced(book.advanced),
  });
  const [bookSaved, setBookSaved] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [allVoices, setAllVoices] = useState<GTSVoice[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [filteredVoices, setFilteredVoices] = useState<GTSVoice[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [voiceError, setVoiceError] = useState("");
  const isEleven = bookCfg.platform === "ElevenLabs";

  const [narrationText, setNarrationText] = useState("");
  const [isLoadedClipText, setIsLoadedClipText] = useState(false);
  const [loadedClipId, setLoadedClipId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [currentBlobUrl, setCurrentBlobUrl] = useState("");
  const [currentBase64, setCurrentBase64] = useState("");
  const [currentAudioEncoding, setCurrentAudioEncoding] = useState<"MP3" | "LINEAR16" | "OGG_OPUS">("LINEAR16");
  const [isSavingClip, setIsSavingClip] = useState(false);

  const [chapterOrder, setChapterOrder] = useState<ChapterOrderEntry[]>([{ name: "Chapter 1", order: 1 }]);
  const [activeChapter, setActiveChapter] = useState("");
  console.log("show current active chapter--->",activeChapter);

  const [newChapter, setNewChapter] = useState("");
  const [renameModal, setRenameModal] = useState<{
    open: boolean;
    from: string;
    value: string;
    order: number;
    saving: boolean;
  }>({ open: false, from: "", value: "", order: 1, saving: false });

  const [clips, setClips] = useState<Clip[]>([]);
  const [loadingClips, setLoadingClips] = useState(true);
  const [loadingAudio, setLoadingAudio] = useState<Record<string, boolean>>({});
  const [deletingClip, setDeletingClip] = useState<Record<string, boolean>>({});
  const [renamingClipId, setRenamingClipId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [ebook, setEbook] = useState(book.ebook ?? null);
  const [ebookBusy, setEbookBusy] = useState(false);
  const ebookInputRef = useRef<HTMLInputElement | null>(null);
  const committingRenameRef = useRef<string | null>(null);
  const clipAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingClip, setPlayingClip] = useState<string | null>(null);
  const { role } = useSelector((state: RootState) => state.auth);
  // Book settings are chosen by an admin; partners may only edit Notes.
  const isAdmin = role === "admin";
  const cfgLocked = !isAdmin;
  type MergedItem = MergedChapterUI & { clipIds: string[]; mergedDownloadExt?: "mp3" | "wav" };
  const [mergedList, setMergedList] = useState<MergedItem[]>([]);
  const [loadingMerged, setLoadingMerged] = useState(true);
  const [reconstructing, setReconstructing] = useState<Record<string, boolean>>({});
  const [deletingMerged, setDeletingMerged] = useState<Record<string, boolean>>({});
  const [isMerging, setIsMerging] = useState(false);
  // console.log("show isMerging State:",isMerging);

  const [mergeMsg, setMergeMsg] = useState("");
  const [pendingMerge, setPendingMerge] = useState<{
    blobUrl: string; chapterName: string; clipIds: string[];
    totalChars: number; clipCount: number;
  } | null>(null);
  const [isSavingMerge, setIsSavingMerge] = useState(false);
  const [playingMerged, setPlayingMerged] = useState<string | null>(null);
  const [downloadingBook, setDownloadingBook] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [activeAudioUrl, setActiveAudioUrl] = useState("");
  const [activeAudioMeta, setActiveAudioMeta] = useState<{ chapter: string; clipName: string } | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState>(CLOSED_CONFIRM);

  const askConfirm = (
    title: string, message: string, confirmLabel: string, onConfirm: () => void
  ) => setConfirm({ open: true, title, message, confirmLabel, onConfirm });

  const closeConfirm = () => setConfirm(CLOSED_CONFIRM);

  const audioRef = useRef<HTMLAudioElement>(null);
  const mergedAudio = useRef<HTMLAudioElement>(null);
  const audioCache = useRef<Record<string, string>>({});

  // ── Fetch base64 (cached) ─────────────────────────────────────────────────  
  const fetchBase64 = useCallback(async (clipId: string): Promise<string> => {
    if (audioCache.current[clipId]) return audioCache.current[clipId];
    const r = await ttsService.getClipAudio(clipId);
    audioCache.current[clipId] = r.audioBase64;
    return r.audioBase64;
  }, []);

  // ── Reconstruct merged audio ──────────────────────────────────────────────
  const reconstruct = useCallback(async (
    mergedId: string, clipIds: string[], encoding: string
  ) => {
    if (!clipIds?.length) return;
    setReconstructing(p => ({ ...p, [mergedId]: true }));
    try {
      let blobUrl = "";
      let mergedDownloadExt: "mp3" | "wav" | undefined;
      try {
        const r = await ttsService.getMergedAudio(mergedId);
        if (r.audioBase64) {
          blobUrl = ttsService.base64ToBlobUrl(r.audioBase64, "MP3");
          mergedDownloadExt = "mp3";
        }
      } catch {
        /* no server merged file (legacy / empty) */
      }
      if (!blobUrl) {
        const base64List = await Promise.all(clipIds.map(id => fetchBase64(id)));
        blobUrl = await mergeAudioClips(base64List, encoding);
        mergedDownloadExt = "wav";
      }
      setMergedList(p => p.map(m => m.id === mergedId ? { ...m, blobUrl, mergedDownloadExt } : m));
      setClips(p => p.map(c => {
        if (clipIds.includes(c.id) && !c.base64) {
          const b64 = audioCache.current[c.id] || "";
          return { ...c, base64: b64, blobUrl: b64 ? ttsService.base64ToBlobUrl(b64, encoding) : "" };
        }
        return c;
      }));
    } catch (err) {
      console.error("[BookEditor] reconstruct failed:", err);
    } finally {
      setReconstructing(p => ({ ...p, [mergedId]: false }));
    }
  }, [fetchBase64]);

  // ── Load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const enc = book.advanced?.audioEncoding || "MP3";

    ttsService.getBookChapters(book._id).then((chapters) => {
      if (!alive || !chapters.length) return;
      setChapterOrder(chapters);
      setActiveChapter((prev) => (chapters.some((entry) => entry.name === prev) ? prev : chapters[chapters.length - 1].name));
      // setActiveChapter((prev) => (chapters.some((entry) => entry.name === prev) ? prev : chapters.length - 1)); 
    }).catch(() => { });

    ttsService.getClipsForBook(book._id).then((dbClips: IClip[]) => {
      if (!alive) return;
      if (dbClips.length > 0) {
        const mapped = dbClips.map(c => ({
          id: c._id, name: c.clipName, chapter: c.chapter,
          blobUrl: "", base64: "", selected: false, chars: c.charCount, savedToDb: true, scriptText: c.scriptText || "",
          createdAt: c.createdAt,
          order: c.order,
        }));
        setClips(sortClipsForDisplay(mapped));
      }
      setLoadingClips(false);
    }).catch(() => alive && setLoadingClips(false));

    ttsService.getMergedForBook(book._id).then(async (dbMerged: IMergedChapter[]) => {
      if (!alive) return;
      const mapped: MergedItem[] = dbMerged.map(m => ({
        id: m._id,
        chapterName: m.chapterName,
        blobUrl: "",
        clipIds: m.clipIds ?? [],
        clipCount: m.clipCount ?? (m.clipIds?.length ?? 0),
        totalChars: m.totalChars ?? 0,
        createdAt: new Date(m.createdAt).toLocaleString(),
        savedToDb: true,
      }));
      setMergedList(mapped);
      setLoadingMerged(false);
      for (const m of dbMerged) {
        if (!alive) break;
        if (m.clipIds?.length) reconstruct(m._id, m.clipIds, enc);
      }
    }).catch(() => alive && setLoadingMerged(false));

    return () => { alive = false; };
  }, [book._id, reconstruct, book.advanced?.audioEncoding]);

  useEffect(() => {
    ttsService.getVoices(undefined, bookCfg.platform).then((v: any) => {
      setAllVoices(v); setLanguages(ttsService.buildLanguageList(v)); setLoadingVoices(false);
    }).catch(() => {
      setVoiceError("Backend offline");
      setLanguages(mergeLanguageOptions(STANDARD_FALLBACK_LANGUAGE_OPTIONS, CORE_LANGUAGE_OPTIONS));
      setAllVoices([]);
      if (bookCfg.platform === "ElevenLabs") {
        setFilteredVoices([]);
      } else {
        setFilteredVoices([{
          name: "lv-LV-Chirp3-HD-Achernar",
          ssmlGender: "FEMALE",
          naturalSampleRateHertz: 48000,
          languageCodes: [...OFFLINE_VOICE_LANGUAGE_CODES],
        }]);
      }
      setLoadingVoices(false);
    });
  }, [bookCfg.platform]);

  useEffect(() => {
    if (!allVoices.length) return;
    const nextVoices = ttsService.filterVoicesForSelection(allVoices, {
      model: bookCfg.model,
      platform: bookCfg.platform,
    });
    setFilteredVoices(nextVoices);
    setBookCfg(p => ({
      ...p,
      realVoice: nextVoices.some(v => v.name === p.realVoice) ? p.realVoice : (nextVoices[0]?.name || ""),
    }));
  }, [bookCfg.model, bookCfg.platform, allVoices]);

  const handleBookCfg = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBookCfg((p: any) => ({ ...p, [name]: value }));
    setBookSaved(false);
  }, []);

  const handleAdvanced = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const boolValue = (e.target as HTMLInputElement).type === "checkbox"
      ? (e.target as HTMLInputElement).checked
      : undefined;
    setBookCfg((p: any) => ({
      ...p,
      advanced: {
        ...p.advanced,
        [name]:
          (name === "audioEncoding" || name === "effectsProfileId")
            ? value
            : name === "useSpeakerBoost"
              ? Boolean(boolValue)
              : Number(value),
      },
    }));
  }, []);

  const handleSaveBook = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const u = await ttsService.updateBook(book._id, { ...bookCfg, advanced: mergeAdvanced(bookCfg.advanced) } as any);
      setBookSaved(true);
      onUpdate(u);
    }
    catch { setBookSaved(true); }
  };

  const handleAddChapter = async () => {
    const name = newChapter.trim() || `Chapter ${chapterOrder.length + 1}`;
    if (chapterOrder.some((entry) => entry.name === name)) return;
    try {
      const result = await ttsService.addBookChapter(book._id, name);
      setChapterOrder(result.chapters);
      setActiveChapter(name);
      setNewChapter("");
    } catch (err: any) {
      setMergeMsg(`⚠ Add chapter failed: ${err?.response?.data?.error || err?.message || "Unknown error"}`);
    }
  };

  const applyChapterUpdate = (chapters: ChapterOrderEntry[], chapterName: string, newName: string) => {
    setChapterOrder(chapters);
    setClips((p) => p.map((c) => (c.chapter === chapterName ? { ...c, chapter: newName } : c)));
    setMergedList((p) => p.map((m) => (m.chapterName === chapterName ? { ...m, chapterName: newName } : m)));
    if (activeChapter === chapterName) setActiveChapter(newName);
    if (pendingMerge?.chapterName === chapterName) {
      setPendingMerge({ ...pendingMerge, chapterName: newName });
    }
  };

  const handleUpdateChapter = async (chapterName: string, nextRaw: string, nextOrder: number) => {
    const newName = nextRaw.trim();
    if (!newName) return;

    const currentOrder = getChapterSerial(chapterOrder, chapterName);
    const orderChanged = nextOrder !== currentOrder;
    if (newName === chapterName && !orderChanged) return;
    if (newName !== chapterName && chapterOrder.some((entry) => entry.name === newName)) {
      setMergeMsg(`⚠ Chapter "${newName}" already exists.`);
      return;
    }

    try {
      const result = await ttsService.renameChapter(book._id, {
        oldName: chapterName,
        newName,
        order: orderChanged ? nextOrder : undefined,
      });
      applyChapterUpdate(result.chapters, chapterName, result.chapterName);
      setMergeMsg(
        orderChanged && newName !== chapterName
          ? `✓ Chapter updated to "${result.chapterName}" (#${result.order})`
          : orderChanged
            ? `✓ Chapter moved to position ${result.order}`
            : `✓ Chapter renamed to "${result.chapterName}"`,
      );
    } catch (err: any) {
      setMergeMsg(`⚠ Update failed: ${err?.response?.data?.error || err?.message || "Unknown error"}`);
    }
  };

  const handleMoveChapter = async (chapterName: string, direction: -1 | 1) => {
    const currentOrder = getChapterSerial(chapterOrder, chapterName);
    const targetOrder = currentOrder + direction;
    if (targetOrder < 1 || targetOrder > chapterOrder.length) return;
    try {
      const result = await ttsService.renameChapter(book._id, {
        oldName: chapterName,
        newName: chapterName,
        order: targetOrder,
      });
      setChapterOrder(result.chapters);
    } catch (err: any) {
      setMergeMsg(`⚠ Reorder failed: ${err?.response?.data?.error || err?.message || "Unknown error"}`);
    }
  };

  // ── Load single clip audio ────────────────────────────────────────────────
  const loadClipAudio = async (clipId: string) => {
    setLoadingAudio(p => ({ ...p, [clipId]: true }));
    try {
      const b64 = await fetchBase64(clipId);
      const blobUrl = ttsService.base64ToBlobUrl(b64, bookCfg.advanced.audioEncoding);
      setClips(p => p.map(c => c.id === clipId ? { ...c, base64: b64, blobUrl } : c));
    } catch (err: any) {
      setMergeMsg(" Failed to load audio: " + (err?.response?.data?.error ?? err.message));
    } finally { setLoadingAudio(p => ({ ...p, [clipId]: false })); }
  };

  const loadAudioForSelected = async () => {
    // setIsMerging(false)
    try {

      const need = clips.filter(c => c.selected && !c.base64);
      if (!need.length) return;
      setMergeMsg(`Loading audio for ${need.length} clip(s)…`);
      await Promise.all(need.map(c => loadClipAudio(c.id)));
      setMergeMsg("Audio loaded! Click Merge.");
      setIsMerging(true)
    } catch (error) {

    } finally {
      setIsMerging(false)
    }
  };

  // ── Generate ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerateError("");
    if (!narrationText.trim()) { setGenerateError("Enter text."); return; }
    if (!bookCfg.realVoice) { setGenerateError("Select a voice."); return; }
    setIsGenerating(true); setCurrentBlobUrl(""); setCurrentBase64("");
    try {
      const r = await ttsService.synthesize({
        text: narrationText, voiceName: bookCfg.realVoice,
        languageCode: bookCfg.language, styleInstructions: bookCfg.styleInstructions,
        platform: bookCfg.platform,
        advanced: mergeAdvanced(bookCfg.advanced),
      });
      const synthEncoding = r.audioEncoding || "LINEAR16";
      const blobUrl = ttsService.base64ToBlobUrl(r.audioContent, synthEncoding);
      setCurrentAudioEncoding(synthEncoding);
      setCurrentBase64(r.audioContent); setCurrentBlobUrl(blobUrl);
      setIsLoadedClipText(false);
      setTimeout(() => audioRef.current?.play(), 100);
    } catch (err: any) {
      setGenerateError(err?.response?.data?.error ?? "Generation failed.");
    } finally { setIsGenerating(false); }
  };

  // ── Save clip ─────────────────────────────────────────────────────────────
  const handleSaveClip = async () => {
    if (!currentBase64 || !currentBlobUrl) return;
    setIsSavingClip(true);
    const editingClip = loadedClipId ? clips.find(c => c.id === loadedClipId) : null;
    const chCount = clips.filter(c => c.chapter === activeChapter).length;
    const resolvedClipName = editingClip?.name || `Clip ${chCount + 1}`;
    try {
      const r = await ttsService.saveClip(book._id, {
        chapter: activeChapter, clipName: resolvedClipName,
        audioBase64: currentBase64, audioEncoding: currentAudioEncoding,
        voiceName: bookCfg.realVoice, languageCode: bookCfg.language,
        model: bookCfg.model, charCount: narrationText.length, scriptText: narrationText,
      });
      if (!chapterOrder.some((entry) => entry.name === activeChapter)) {
        const refreshed = await ttsService.getBookChapters(book._id);
        setChapterOrder(refreshed);
      }
      const savedClip = {
        id: String(r.clipId),
        name: r.clipName || resolvedClipName,
        chapter: r.chapter || activeChapter,
        blobUrl: currentBlobUrl,
        base64: currentBase64,
        selected: false,
        chars: narrationText.length,
        savedToDb: true,
        scriptText: narrationText,
        createdAt: editingClip?.createdAt || new Date().toISOString(),
        order: r?.order ?? editingClip?.order
      };
      audioCache.current[savedClip.id] = currentBase64;
      setClips(p => sortClipsForDisplay(editingClip
        ? p.map(c => c.id === editingClip.id ? savedClip : c)
        : [...p, savedClip]
      ));
      if (editingClip) {
        setMergeMsg(`✓ "${savedClip.name}" updated.`);
      }
    } catch {
      if (editingClip) {
        setClips(p => p.map(c => c.id === editingClip.id ? {
          ...c,
          blobUrl: currentBlobUrl,
          base64: currentBase64,
          chars: narrationText.length,
          savedToDb: false,
          scriptText: narrationText,
        } : c));
        setMergeMsg(`⚠ "${resolvedClipName}" updated locally only (DB save failed).`);
      } else {
        setClips(p => sortClipsForDisplay([...p, {
          id: `local-${Date.now()}`, name: resolvedClipName,
          chapter: activeChapter, blobUrl: currentBlobUrl, base64: currentBase64,
          selected: false, chars: narrationText.length, savedToDb: false, scriptText: narrationText,
          createdAt: new Date().toISOString(),
        }]));
      }
    } finally {
      setCurrentBlobUrl(""); setCurrentBase64(""); setNarrationText(""); setIsLoadedClipText(false); setLoadedClipId(null); setIsSavingClip(false);
    }
  };

  // ── Merge ─────────────────────────────────────────────────────────────────
  const handleMerge = async () => {
    const sel = clips.filter(c => c.selected);
    if (!sel.length) { setMergeMsg("Select clips."); return; }
    // console.log("show sele varible---->",sel);

    const chapSet: Record<string, boolean> = {};
    sel.forEach(c => { chapSet[c.chapter] = true; });
    const uniq = Object.keys(chapSet);
    if (uniq.length > 1) { setMergeMsg(" One chapter at a time."); return; }
    const needAudio = sel.filter(c => !c.base64);
    // console.log("show need audio -->",needAudio);

    if (needAudio.length) {
      setMergeMsg(`⚠ ${needAudio.length} clip(s) need audio. Click "Load Audio".`); return;

    }
    setIsMerging(true); setPendingMerge(null); setMergeMsg("Merging…");
    try {
      const blobUrl = await mergeAudioClips(sel.map(c => c.base64), bookCfg.advanced.audioEncoding);
      setPendingMerge({
        blobUrl, chapterName: uniq[0],
        clipIds: sel.map(c => c.id),
        totalChars: sel.reduce((s, c) => s + c.chars, 0),
        clipCount: sel.length,
      });
      setClips(p => p.map(c => ({ ...c, selected: false })));
      setMergeMsg("✓ Merged! Preview & Save below.");
    } catch (err: any) {
      setMergeMsg("⚠ " + (err?.message ?? "Merge failed"));
    } finally { setIsMerging(false); }
  };

  // ── Save merged ───────────────────────────────────────────────────────────
  const handleSaveMerged = async () => {
    if (!pendingMerge) return;
    setIsSavingMerge(true);
    try {
      const r = await ttsService.mergeClips(book._id, {
        clipIds: pendingMerge.clipIds,
        chapterName: pendingMerge.chapterName,
      });
      let blobUrl = pendingMerge.blobUrl;
      let mergedDownloadExt: "mp3" | "wav" = "wav";
      try {
        const audio = await ttsService.getMergedAudio(String(r.mergedId));
        if (audio.audioBase64) {
          blobUrl = ttsService.base64ToBlobUrl(audio.audioBase64, "MP3");
          mergedDownloadExt = "mp3";
        }
      } catch {
        /* keep browser WAV preview if merged file not readable yet */
      }
      setMergedList(p => [...p, {
        id: String(r.mergedId),
        chapterName: r.chapterName,
        blobUrl,
        mergedDownloadExt,
        clipIds: pendingMerge.clipIds,
        clipCount: r.clipCount,
        totalChars: r.totalChars,
        createdAt: new Date().toLocaleString(),
        savedToDb: true,
      }]);
      setPendingMerge(null);
      setMergeMsg(`✓ "${r.chapterName}" saved!`);
    } catch (err: any) {
      setMergeMsg("⚠ Save failed: " + (err?.message ?? ""));
    } finally { setIsSavingMerge(false); }
  };

  // ── Delete merged — uses ConfirmModal ─────────────────────────────────────
  const handleDeleteMerged = (mergedId: string, chapterName: string) => {
    askConfirm(
      "Delete merged chapter",
      `"${chapterName}" will be permanently removed from the database. This cannot be undone.`,
      "Delete",
      async () => {
        closeConfirm();
        setDeletingMerged(p => ({ ...p, [mergedId]: true }));
        try {
          await ttsService.deleteMerged(mergedId);
          setMergedList(p => p.filter(m => m.id !== mergedId));
          setMergeMsg(`✓ "${chapterName}" deleted.`);
        } catch (err: any) {
          setMergeMsg(" Delete failed: " + (err?.message ?? ""));
        } finally {
          setDeletingMerged(p => ({ ...p, [mergedId]: false }));
        }
      }
    );
  };

  // ── Ebook attachment ────────────────────────────────────────────
  const EBOOK_ACCEPT = ".epub,.fb2,.pdf";
  const EBOOK_MAX_BYTES = 25 * 1024 * 1024;

  const formatBytes = (bytes: number): string =>
    bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  const handleEbookPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear immediately so re-picking the same file still fires onChange.
    e.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["epub", "fb2", "pdf"].includes(ext)) {
      toast.error("Only .epub, .fb2 and .pdf files are allowed.");
      return;
    }
    if (file.size > EBOOK_MAX_BYTES) {
      toast.error(`File is too large (max ${EBOOK_MAX_BYTES / (1024 * 1024)}MB).`);
      return;
    }

    setEbookBusy(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Could not read the file"));
        reader.onload = () => {
          const result = String(reader.result ?? "");
          resolve(result.slice(result.indexOf(",") + 1));
        };
        reader.readAsDataURL(file);
      });

      const updated = await ttsService.uploadBookEbook(book._id, { fileName: file.name, dataBase64 });
      setEbook(updated.ebook ?? null);
      onUpdate(updated);
      toast.success(`"${file.name}" attached.`);
    } catch (err: any) {
      toast.error("Upload failed: " + (err?.response?.data?.error ?? err?.message ?? ""));
    } finally {
      setEbookBusy(false);
    }
  };

  const handleEbookDownload = async () => {
    setEbookBusy(true);
    try {
      const { url } = await ttsService.getBookEbookUrl(book._id);
      // Signed URL already carries a download disposition.
      window.location.href = url;
    } catch (err: any) {
      toast.error("Download failed: " + (err?.response?.data?.error ?? err?.message ?? ""));
    } finally {
      setEbookBusy(false);
    }
  };

  const handleEbookRemove = () => {
    if (!ebook) return;
    askConfirm(
      "Remove ebook",
      `"${ebook.fileName}" will be permanently removed from this book.`,
      "Remove",
      async () => {
        closeConfirm();
        setEbookBusy(true);
        try {
          const updated = await ttsService.deleteBookEbook(book._id);
          setEbook(updated.ebook ?? null);
          onUpdate(updated);
          toast.success("Ebook removed.");
        } catch (err: any) {
          toast.error("Remove failed: " + (err?.response?.data?.error ?? err?.message ?? ""));
        } finally {
          setEbookBusy(false);
        }
      },
    );
  };

  // ── Rename clip ────────────────────────────────────────────────────
  const startRenameClip = (clip: Clip) => {
    setRenamingClipId(clip.id);
    setRenameDraft(clip.name);
  };

  const cancelRenameClip = () => {
    setRenamingClipId(null);
    setRenameDraft("");
  };

  const commitRenameClip = async (clip: Clip) => {
    // Enter unmounts the input, which can also fire onBlur — guard against a
    // duplicate PATCH for the same clip.
    if (committingRenameRef.current === clip.id) return;

    const nextName = renameDraft.trim();
    if (!nextName || nextName === clip.name) {
      cancelRenameClip();
      return;
    }

    committingRenameRef.current = clip.id;

    const prevName = clip.name;
    // Optimistic — revert if the API rejects it.
    setClips(p => p.map(c => (c.id === clip.id ? { ...c, name: nextName } : c)));
    setRenamingClipId(null);
    setSavingRename(true);
    try {
      if (clip.savedToDb) {
        await ttsService.renameClip(clip.id, nextName);
      }
      setMergeMsg(`✓ Renamed to "${nextName}".`);
    } catch (err: any) {
      setClips(p => p.map(c => (c.id === clip.id ? { ...c, name: prevName } : c)));
      setMergeMsg("⚠ Rename failed: " + (err?.response?.data?.error ?? err?.message ?? ""));
    } finally {
      committingRenameRef.current = null;
      setSavingRename(false);
      setRenameDraft("");
    }
  };

  const handleDeleteClip = (clip: Clip) => {
    askConfirm(
      "Delete clip",
      `"${clip.name}" will be permanently removed. This cannot be undone.`,
      "Delete",
      async () => {
        closeConfirm();
        setDeletingClip((p) => ({ ...p, [clip.id]: true }));
        try {
          if (clip.savedToDb) {
            await ttsService.deleteClip(clip.id);
          }
          if (playingClip === clip.id) {
            clipAudioRef.current?.pause();
            clipAudioRef.current = null;
            setPlayingClip(null);
          }
          if (loadedClipId === clip.id) {
            setNarrationText("");
            setIsLoadedClipText(false);
            setLoadedClipId(null);
            setGenerateError("");
          }
          delete audioCache.current[clip.id];
          setClips((p) => p.filter((c) => c.id !== clip.id));
          setMergeMsg(`✓ "${clip.name}" deleted.`);
        } catch (err: any) {
          setMergeMsg("⚠ Delete failed: " + (err?.response?.data?.error ?? err?.message ?? ""));
        } finally {
          setDeletingClip((p) => ({ ...p, [clip.id]: false }));
        }
      }
    );
  };

  // ── Download full book ────────────────────────────────────────────────────
  const handleDownloadBook = async () => {
    if (!mergedList.length) {
      setMergeMsg("No merged chapters yet.");
      return;
    }

    setDownloadingBook(true);

    const sorted = [...mergedList].sort((a, b) => {
      const rankA = getChapterSerial(chapterOrder, a.chapterName) || 999;
      const rankB = getChapterSerial(chapterOrder, b.chapterName) || 999;
      if (rankA !== rankB) return rankA - rankB;
      return a.chapterName.localeCompare(b.chapterName);
    });

    try {
      const JSZipModule = await import("jszip");
      const JSZip = (JSZipModule as any).default || JSZipModule;
      const zip = new JSZip();
      const folder = zip.folder(safe(book.title))!;

      for (let i = 0; i < sorted.length; i++) {
        const m = sorted[i];
        const index = String(i + 1).padStart(2, "0");

        setMergeMsg(`Building ${index}/${sorted.length}: ${m.chapterName}…`);

        if (!m.clipIds?.length) continue;

        let wrote = false;
        try {
          const { audioBase64 } = await ttsService.getMergedAudio(m.id);
          if (audioBase64) {
            const binary = atob(audioBase64);
            const bytes = new Uint8Array(binary.length);
            for (let j = 0; j < binary.length; j++) {
              bytes[j] = binary.charCodeAt(j);
            }
            folder.file(`${index} - ${safe(m.chapterName)}.mp3`, bytes);
            wrote = true;
          }
        } catch {
          /* fall back to raw clip concat */
        }

        if (!wrote) {
          const base64List = await Promise.all(
            m.clipIds.map(id => fetchBase64(id))
          );

          const byteArrays = base64List.map(b64 => {
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let j = 0; j < binary.length; j++) {
              bytes[j] = binary.charCodeAt(j);
            }
            return bytes;
          });

          const totalLen = byteArrays.reduce((s, a) => s + a.length, 0);
          const combined = new Uint8Array(totalLen);
          let offset = 0;
          for (const arr of byteArrays) {
            combined.set(arr, offset);
            offset += arr.length;
          }

          folder.file(`${index} - ${safe(m.chapterName)}.wav`, combined);
        }
      }

      setMergeMsg("Compressing…");

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipUrl = URL.createObjectURL(zipBlob);

      const a = document.createElement("a");
      a.href = zipUrl;
      a.download = `${safe(book.title)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(zipUrl), 3000);
      setMergeMsg(`✓ "${book.title}.zip" — ${sorted.length} chapter${sorted.length !== 1 ? "s" : ""} downloaded!`);
    } catch (err: any) {
      console.error("[DownloadBook]", err);
      setMergeMsg("⚠ Download failed: " + (err?.message ?? String(err)));
    } finally {
      setDownloadingBook(false);
    }
  };

  const playMerged = (m: MergedItem) => {
    if (!m.blobUrl) return;
    if (playingMerged === m.id) { mergedAudio.current?.pause(); setPlayingMerged(null); return; }
    setPlayingMerged(m.id);
    if (mergedAudio.current) {
      mergedAudio.current.src = m.blobUrl;
      mergedAudio.current.play();
      mergedAudio.current.onended = () => setPlayingMerged(null);
    }
  };

  const toggleClip = (id: string) => {
    const clip = clips.find(c => c.id === id);

    const willUncheck = !!clip?.selected;
    if (willUncheck && loadedClipId === id) {
      setNarrationText("");
      setIsLoadedClipText(false);
      setLoadedClipId(null);
      setGenerateError("");
      setMergeMsg("Loaded clip text cleared.");
    }
    setClips(p => p.map(c => c.id === id ? { ...c, selected: !c.selected } : c));

  };
  // console.log("show clips-=-->",clips);


  const toggleAllInChapter = (ch: string) => {

    const chClips = clips.filter(c => c.chapter === ch);
    const allSel = chClips.every(c => c.selected);
    if (allSel && loadedClipId && chClips.some(c => c.id === loadedClipId)) {
      setNarrationText("");
      setIsLoadedClipText(false);
      setLoadedClipId(null);
      setGenerateError("");
      setMergeMsg("Loaded clip text cleared.");
    }
    setClips(p => p.map(c => c.chapter === ch ? { ...c, selected: !allSel } : c));
  };

  let selectedCount = clips.filter(c => c.selected).length ?? 0;
  let selectedNeedAudio = clips.filter(c => c.selected && !c.base64).length;
  // console.log("show select need audio varible---->",selectedNeedAudio);
  // console.log("show select count varible-->",selectedCount);


  const chaptersWithoutClips = chapterOrder
    .map((entry) => entry.name)
    .filter((ch) => !clips.some((c) => c.chapter === ch));
  const orderedChapters = orderedChapterNames(chapterOrder, clips, chaptersWithoutClips);
  const groupedClips = orderedChapters.reduce<Record<string, Clip[]>>((acc, chapter) => {
    const chClips = sortClipsForDisplay(clips.filter(c => c.chapter === chapter));
    if (chClips.length) acc[chapter] = chClips;
    return acc;
  }, {});
  const orderedMergedList = [...mergedList].sort((a, b) => {
    const rankA = getChapterSerial(chapterOrder, a.chapterName) || 999;
    const rankB = getChapterSerial(chapterOrder, b.chapterName) || 999;
    if (rankA !== rankB) return rankA - rankB;
    return a.chapterName.localeCompare(b.chapterName);
  });
  const totalChars = clips.reduce((s, c) => s + c.chars, 0);
  const allMergedReady = mergedList.length > 0 && mergedList.some(m => m.blobUrl);

  const handlePlayClip = (clipId: string, blobUrl: string, chapter: string, clipName: string) => {
    if (playingClip === clipId) {
      clipAudioRef.current?.pause();
      setPlayingClip(null);
      setAudioOpen(false);
      setActiveAudioUrl("");
      setActiveAudioMeta(null);
      return;
    }
    if (clipAudioRef.current) {
      clipAudioRef.current.pause();
      clipAudioRef.current = null;
    }
    setPlayingClip(clipId);
    setActiveAudioUrl(blobUrl);
    setActiveAudioMeta({ chapter, clipName });
    setAudioOpen(true);
  };

  const handlePreviewClipText = (clip: Clip) => {
    if (!clip.scriptText?.trim()) {
      setMergeMsg(`⚠ No saved text found for "${clip.name}".`);
      return;
    }
    setActiveChapter(clip.chapter);
    setNarrationText(clip.scriptText.slice(0, 8000));
    setIsLoadedClipText(true);
    setLoadedClipId(clip.id);
    setGenerateError("");
    setMergeMsg(`✓ Loaded saved text from "${clip.name}".`);
  };

  return (
    <div>
      {/* ── Audio Player Popup ── */}
      {audioOpen && activeAudioUrl && (
        <div className="ap-popup">
          <button className="ap-popup__close" onClick={() => {
            setAudioOpen(false);
            setActiveAudioUrl("");
            setActiveAudioMeta(null);
            setPlayingClip(null);
          }}>✕</button>
          {activeAudioMeta && (
            <div className="ap-popup__meta">
              <span className="ap-popup__chapter">{activeAudioMeta.chapter}</span>
              <span className="ap-popup__clip">{activeAudioMeta.clipName}</span>
            </div>
          )}
          <AudioPlayer audioUrl={activeAudioUrl} autoPlay skipSeconds={10} />
        </div>
      )}
      {confirm.open && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onCancel={closeConfirm}
        />
      )}

      {renameModal.open && (
        <div className="modal-overlay" onClick={() => !renameModal.saving && setRenameModal({ open: false, from: "", value: "", order: 1, saving: false })}>
          <div className="modal-box chapter-rename-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Edit Chapter</div>
            <div className="modal-sub">Update chapter name and display order across clips and merged chapters.</div>
            <div className="cp-field" style={{ marginBottom: 16 }}>
              <div className="cp-field__label-row">
                <span className="cp-field__label">Chapter number</span>
              </div>
              <input
                type="number"
                min={1}
                max={chapterOrder.length}
                value={renameModal.order}
                onChange={(e) => setRenameModal((p) => ({
                  ...p,
                  order: Math.max(1, Math.min(chapterOrder.length, Number(e.target.value) || 1)),
                }))}
                disabled={renameModal.saving}
              />
            </div>
            <div className="cp-field" style={{ marginBottom: 16 }}>
              <div className="cp-field__label-row">
                <span className="cp-field__label">Chapter name</span>
              </div>
              <input
                type="text"
                value={renameModal.value}
                onChange={(e) => setRenameModal((p) => ({ ...p, value: e.target.value }))}
                autoFocus
                disabled={renameModal.saving}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (renameModal.saving) return;
                    setRenameModal((p) => ({ ...p, saving: true }));
                    await handleUpdateChapter(renameModal.from, renameModal.value, renameModal.order);
                    setRenameModal({ open: false, from: "", value: "", order: 1, saving: false });
                  }
                }}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn--cancel"
                disabled={renameModal.saving}
                onClick={() => setRenameModal({ open: false, from: "", value: "", order: 1, saving: false })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-btn modal-btn--save"
                disabled={renameModal.saving || !renameModal.value.trim()}
                onClick={async () => {
                  setRenameModal((p) => ({ ...p, saving: true }));
                  await handleUpdateChapter(renameModal.from, renameModal.value, renameModal.order);
                  setRenameModal({ open: false, from: "", value: "", order: 1, saving: false });
                }}
              >
                {renameModal.saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Breadcrumb */}
      <div className="be-breadcrumb">
        <button className="be-back" onClick={onBack}>← All Projects</button>
        <span className="be-sep">/</span>
        <span className="be-crumb-proj">{project.projectName}</span>
        <span className="be-sep">/</span>
        <span className="be-crumb-book">{book.title}</span>
        {bookSaved && <span className="cp-pill cp-pill--green" style={{ marginLeft: 12 }}>✓ Saved</span>}

        <div className="be-ebook">
          {ebook ? (
            <>
              <span className="be-ebook__name" title={ebook.fileName}>{ebook.fileName}</span>
              <span className="be-ebook__size">{formatBytes(ebook.size)}</span>
              <button type="button" className="cp-clip-load" disabled={ebookBusy} onClick={handleEbookDownload}>
                {ebookBusy ? <span className="cp-spinner" /> : "⬇ Download"}
              </button>
              {isAdmin && (
                <>
                  <button type="button" className="cp-clip-load" disabled={ebookBusy}
                    onClick={() => ebookInputRef.current?.click()}>
                    Replace
                  </button>
                  <button type="button" className="cp-clip-load cp-clip-delete" disabled={ebookBusy}
                    onClick={handleEbookRemove}>
                    Remove
                  </button>
                </>
              )}
            </>
          ) : isAdmin ? (
            <button type="button" className="cp-clip-load" disabled={ebookBusy}
              onClick={() => ebookInputRef.current?.click()}>
              {ebookBusy ? <span className="cp-spinner">Uploading…</span> : "⬆ Attach Ebook File"}
            </button>
          ) : null}

          {isAdmin && (
            <input
              ref={ebookInputRef}
              type="file"
              accept={EBOOK_ACCEPT}
              style={{ display: "none" }}
              onChange={handleEbookPick}
            />
          )}
        </div>
      </div>

      <div className="cp-grid">

        <div className="cp-panel">
          <div className="cp-ptitle"><span className="cp-ptitle__dot" />Book Configuration</div>
          {cfgLocked && (
            <div className="cp-locked-note">
              Set by an admin — you can edit Notes only.
            </div>
          )}
          <form onSubmit={handleSaveBook}>
            <fieldset className="cp-fieldset" disabled={cfgLocked}>
            <FormField label="Book Title" badge="FIELD">
              <input type="text" name="title" value={bookCfg.title} onChange={handleBookCfg} placeholder="e.g. Harry Potter – Book 1" required />
            </FormField>
            <FormField label="Author" badge="FIELD">
              <input type="text" name="author" value={bookCfg.author} onChange={handleBookCfg} placeholder="e.g. J.K. Rowling" />
            </FormField>
            {isAdmin && (
            <FormField label="TTS Platform" badge="SELECTOR">
              <select name="platform" value={bookCfg.platform} onChange={handleBookCfg} disabled>
                {role && role === "admin" ? (
                  <>
                    <option value="ElevenLabs">ElevenLabs</option>
                    <option value="Vertex AI">Vertex AI</option>
                  </>
                ) : (
                  <>
                    <option>Default Platform</option>
                  </>

                )}

              </select>
            </FormField>
            )}
            {!isEleven && (
              <FormField label="Model" badge="SELECTOR">
                <select name="model" value={bookCfg.model} onChange={handleBookCfg}>
                  {ALL_MODELS.map((m: any) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </FormField>
            )}
            <FormField label="Language" badge="SELECTOR/LIST" hint={loadingVoices ? "Loading…" : `${languages.length} languages`}>
              {loadingVoices
                ? <span className="cp-spinner">Fetching…</span>
                : <select name="language" value={bookCfg.language} onChange={handleBookCfg}>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              }
            </FormField> 
            {isAdmin && (
            <FormField label="TTS Real Voice" badge="SELECTOR/LIST" hint={`${filteredVoices.length} voices`}>
              <select name="realVoice" value={bookCfg.realVoice} onChange={handleBookCfg}   
                required disabled={loadingVoices || !filteredVoices.length}>
                <option value="">{!filteredVoices.length ? "No voices" : "Select voice…"}</option>
                {filteredVoices.map(v => (
                  <option key={v.name} value={v.name}>
                    {v.displayName || v.name} ({v.ssmlGender === "FEMALE" ? "F" : v.ssmlGender === "MALE" ? "M" : "N"}) {v.style ? `- ${v.style}` : ""}
                  </option>
                ))}
              </select>
              {voiceError && <span className="cp-err">{voiceError}</span>}
            </FormField>
            )}
            <FormField label="Voice Label" badge="FIELD">
              <input type="text" name="voiceLabel" value={bookCfg.voiceLabel} onChange={handleBookCfg} placeholder="e.g. Narrator Anna" />
            </FormField>
            <FormField
              label="Additional prompt"
              badge="FIELD"
              hint="Optional — appended after the built‑in narrator instructions on the server."
            >
              <textarea name="styleInstructions" value={bookCfg.styleInstructions} onChange={handleBookCfg}
                placeholder="Extra tone or delivery notes…" rows={2} />
            </FormField>
            </fieldset>
            <FormField
              label="Notes"
              badge="FIELD"
              hint="Add some notes your book config"

            >

              <textarea
                className="cp-notes-ta"
                name="notes"
                value={bookCfg.notes}
                onChange={handleBookCfg}
                placeholder="Extra things or delivery notes…"
                rows={8}
                maxLength={1000}
              />
              <small
                style={{
                  // color: (bookCfg.notes?.length ?? 0) > 300
                  //   ? "#ef4444"
                  //   : "#9ca3af",  
                  fontSize: 11,
                }}
              >
                {bookCfg.notes?.length ?? 0}/1000  
              </small>
            </FormField>
            <div className="cp-divider" />
            <button type="button" className="cp-adv-toggle" onClick={() => setShowAdvanced(v => !v)}>
              <span className={`cp-adv-arrow ${showAdvanced ? "cp-adv-arrow--open" : ""}`}>▸</span>
              Advanced Options
            </button>
            {showAdvanced && (
              <fieldset className="cp-fieldset cp-adv-body" disabled={cfgLocked}>
                {isEleven ? (
                  <>
                    <div className="cp-2col">
                      <FormField label="Stability" hint="0 – 100">
                        <input type="number" name="stability" value={bookCfg.advanced.stability} onChange={handleAdvanced} min={0} max={100} step={1} />
                      </FormField>
                      <FormField label="Similarity" hint="0 – 100">
                        <input type="number" name="similarityBoost" value={bookCfg.advanced.similarityBoost} onChange={handleAdvanced} min={0} max={100} step={1} />
                      </FormField>
                    </div>
                    <FormField label="Style" hint="0 – 100">
                      <input type="number" name="style" value={bookCfg.advanced.style} onChange={handleAdvanced} min={0} max={100} step={1} />
                    </FormField>
                    <FormField label="Speaker Boost">
                      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input type="checkbox" name="useSpeakerBoost" checked={!!bookCfg.advanced.useSpeakerBoost} onChange={handleAdvanced} />
                        Enable speaker boost
                      </label>
                    </FormField>
                  </>
                ) : (
                  <>
                    <FormField label="Speaking Rate" hint="0.25 – 4.0">
                      <div className="cp-range-row">
                        <input type="range" name="speakingRate" min={0.25} max={4.0} step={0.05} value={bookCfg.advanced.speakingRate} onChange={handleAdvanced} />
                        <span className="cp-range-val">{bookCfg.advanced.speakingRate.toFixed(2)}×</span>
                      </div>
                    </FormField>
                    <FormField label="Pitch" hint="-20 – +20">
                      <div className="cp-range-row">
                        <input type="range" name="pitch" min={-20} max={20} step={0.5} value={bookCfg.advanced.pitch} onChange={handleAdvanced} />
                        <span className="cp-range-val">{bookCfg.advanced.pitch > 0 ? `+${bookCfg.advanced.pitch}` : bookCfg.advanced.pitch}st</span>
                      </div>
                    </FormField>
                    <FormField label="Volume Gain" hint="-96 – +16">
                      <div className="cp-range-row">
                        <input type="range" name="volumeGainDb" min={-96} max={16} step={1} value={bookCfg.advanced.volumeGainDb} onChange={handleAdvanced} />
                        <span className="cp-range-val">{bookCfg.advanced.volumeGainDb}dB</span>
                      </div>
                    </FormField>
                    <div className="cp-2col">
                      <FormField label="Temperature" hint="0.01 – 2.0">
                        <input
                          type="number"
                          name="temperature"
                          value={bookCfg.advanced.temperature}
                          onChange={handleAdvanced}
                          min={0.01}
                          max={2}
                          step={0.01}
                        />
                      </FormField>
                      <FormField label="Top-P" hint="0 – 1">
                        <input
                          type="number"
                          name="topP"
                          value={bookCfg.advanced.topP}
                          onChange={handleAdvanced}
                          min={0}
                          max={1}
                          step={0.01}
                        />
                      </FormField>
                    </div>
                    <FormField label="Top-K" hint="1+">
                      <input
                        type="number"
                        name="topK"
                        value={bookCfg.advanced.topK}
                        onChange={handleAdvanced}
                        min={1}
                        step={1}
                      />
                    </FormField>
                    <div className="cp-2col">
                      <FormField label="Encoding">
                        <select name="audioEncoding" value={bookCfg.advanced.audioEncoding} onChange={handleAdvanced}>
                          {AUDIO_ENCODINGS.map((e: any) => <option key={e} value={e}>{e}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Sample Rate">
                        <input type="number" name="sampleRateHertz" value={bookCfg.advanced.sampleRateHertz} onChange={handleAdvanced} min={8000} max={48000} step={4000} />
                      </FormField>
                    </div>
                    <FormField label="Effects Profile">
                      <select name="effectsProfileId" value={bookCfg.advanced.effectsProfileId} onChange={handleAdvanced}>
                        {EFFECTS_PROFILES.map((p: any) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </FormField>
                  </>
                )}
              </fieldset>
            )}
            <div className="cp-divider" />
            <button type="submit" className="cp-btn cp-btn--save">Save Book Config</button>
          </form>
        </div>

        <div className="cp-panel">
          <div className="cp-ptitle"><span className="cp-ptitle__dot" />Chapter &amp; Narration</div>

          <div className="cp-section-label">Active Chapter</div>
          <div className="cp-chapters">
            {orderedChapters.map((ch) => {
              const serial = getChapterSerial(chapterOrder, ch);
              const canMoveUp = serial > 1;
              const canMoveDown = serial < chapterOrder.length;
              // console.log("show active chapter");

              return (
                <div key={ch} className="cp-chapter-chip">
                  <button className={`cp-ch-tab ${activeChapter === ch ? "cp-ch-tab--active" : ""}`}
                    onClick={() => {


                      setActiveChapter(ch)
                      setNarrationText("")
                      setAudioOpen(false);
                      setActiveAudioUrl("");
                      setPlayingClip(null);
                      if (clipAudioRef.current) { clipAudioRef.current.pause(); clipAudioRef.current = null; }
                      setClips((prev) =>
                        prev.map((clip) => ({
                          ...clip,
                          selected: false
                        }))
                      )


                    }




                    }>
                    {serial}. {ch} ({clips.filter(c => c.chapter === ch).length})
                  </button>
                  <div className="cp-chapter-chip__actions">
                    <button
                      type="button"
                      className="cp-ch-order-btn"
                      onClick={() => handleMoveChapter(ch, -1)}
                      disabled={!canMoveUp}
                      title="Move chapter up"
                      aria-label={`Move ${ch} up`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="cp-ch-order-btn"
                      onClick={() => handleMoveChapter(ch, 1)}
                      disabled={!canMoveDown}
                      title="Move chapter down"
                      aria-label={`Move ${ch} down`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="cp-ch-rename-btn"
                      onClick={() => setRenameModal({
                        open: true,
                        from: ch,
                        value: ch,
                        order: serial || 1,
                        saving: false,
                      })}
                      title={`Edit ${ch}`}
                      aria-label={`Edit ${ch}`}
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                        <path
                          d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm18-11.5a1 1 0 0 0 0-1.41l-1.34-1.34a1 1 0 0 0-1.41 0l-1.04 1.04 3.75 3.75L21 5.75z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="cp-ch-add">
            <input type="text" placeholder="New chapter name…" value={newChapter}
              onChange={e => setNewChapter(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAddChapter()} />
            <button onClick={handleAddChapter}>＋ Add</button>
          </div>

          <div className="cp-active-ch-label">▸ Pasting for: {activeChapter}</div>
          <div className="cp-ta-wrap">
            <textarea value={narrationText}
              onChange={e => {
                setNarrationText(e.target.value.slice(0, 8000));
                setIsLoadedClipText(false);
              }}
              placeholder={`Paste up to 8 000 chars for ${activeChapter}…`} />
            <div className="cp-char-bar">
              <div className="cp-char-track">
                <div className="cp-char-fill" style={{
                  width: `${(narrationText.length / 8000) * 100}%`,
                  background: narrationText.length > 7200 ? "var(--danger)" : narrationText.length > 5600 ? "var(--warn)" : "var(--accent)",
                }} />
              </div>
              <span className={`cp-char-count ${narrationText.length > 7200 ? "cp-char-count--warn" : ""}`}>
                {narrationText.length.toLocaleString()} / 8 000
              </span>
            </div>
          </div>

          {generateError && <div className="cp-err" style={{ marginTop: 8 }}>⚠ {generateError}</div>}
          {isLoadedClipText && (
            <div className="cp-merge-hint" style={{ marginTop: 8 }}>
              Edit this loaded clip text to enable generating a new clip.
            </div>
          )}
          <button className="cp-btn cp-btn--gen" onClick={handleGenerate} disabled={isGenerating || !narrationText.trim() || isLoadedClipText}>
            {isGenerating ? "⏳  Generating…" : `▶  Generate → ${activeChapter}`}
          </button>

          {currentBlobUrl && (
            <div className="cp-audio">
              <div className="cp-audio__label">Preview · {activeChapter}</div>
              <audio ref={audioRef} controls src={currentBlobUrl} />
              <button className="cp-btn cp-btn--save-clip" onClick={handleSaveClip} disabled={isSavingClip}>
                {isSavingClip ? "⏳ Saving…" : `↑ Save to ${activeChapter} & clear`}
              </button>
            </div>
          )}

          {/* Merged Chapters */}
          <div className="cp-divider" style={{ marginTop: 16 }} />
          <div className="cp-merged-header">
            <div className="cp-merged-title">
              Merged Chapters
              {loadingMerged && <span className="cp-spinner" style={{ marginLeft: 8 }}>Loading…</span>}
            </div>
            {allMergedReady && (
              <button className="cp-btn-download-book" onClick={handleDownloadBook} disabled={downloadingBook}>
                {downloadingBook ? <><span className="cp-spinner" /> Building…</> : "⬇ Download Full Book"}
              </button>
            )}
          </div>
          <audio ref={mergedAudio} style={{ display: "none" }} />

          {pendingMerge && (
            <div className="cp-pending-merge">
              <div className="cp-pending-merge__label">⏳ Ready: {pendingMerge.chapterName}</div>
              <div className="cp-pending-merge__meta">{pendingMerge.clipCount} clips · {pendingMerge.totalChars.toLocaleString()} chars</div>
              <audio controls src={pendingMerge.blobUrl} style={{ width: "100%", height: 32, margin: "8px 0" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="cp-btn cp-btn--save-merged" onClick={handleSaveMerged} disabled={isSavingMerge}>
                  {isSavingMerge ? "⏳ Saving…" : "↑ Save Merged Chapter to DB"}
                </button>
                <button className="cp-btn cp-btn--discard" onClick={() => { setPendingMerge(null); setMergeMsg(""); }}>✕</button>
              </div>
            </div>
          )}

          {!loadingMerged && !mergedList.length && !pendingMerge && (
            <div className="cp-empty">No merged chapters yet.</div>
          )}

          {orderedMergedList.map(m => {
            const isRec = !!reconstructing[m.id];
            const isDeleting = !!deletingMerged[m.id];
            const hasAudio = !!m.blobUrl;
            return (
              <div key={m.id} className="cp-merged-item">
                <div className="cp-merged-item__top">
                  <div className="cp-merged-item__info">
                    <div className="cp-merged-item__name">
                      {getChapterSerial(chapterOrder, m.chapterName)}. {m.chapterName}
                      <span className="cp-clip-badge cp-clip-badge--db" style={{ marginLeft: 8 }}>DB</span>
                    </div>
                    <div className="cp-merged-item__meta">
                      {m.clipCount} clips · {m.totalChars.toLocaleString()} chars · {m.createdAt}
                    </div>
                    {isRec && <div className="cp-merged-rebuilding"><span className="cp-spinner" /> Rebuilding…</div>}
                  </div>
                  <div className="cp-merged-actions">
                    {hasAudio && (
                      <>
                        <button className={`cp-merged-btn cp-merged-btn--play ${playingMerged === m.id ? "cp-merged-btn--playing" : ""}`}
                          onClick={() => playMerged(m)}>
                          {playingMerged === m.id ? "⏹ Stop" : "▶ Play"}
                        </button>
                        <button className="cp-merged-btn cp-merged-btn--download"
                          onClick={() => downloadBlob(m.blobUrl, `${safe(book.title)} - ${safe(m.chapterName)}.${m.mergedDownloadExt ?? "mp3"}`)}>
                          ⬇ Download
                        </button>
                      </>
                    )}
                    <button className="cp-merged-btn cp-merged-btn--remerge" disabled={isRec}
                      onClick={() => reconstruct(m.id, m.clipIds, bookCfg.advanced.audioEncoding)}>
                      {isRec ? "…" : "↺ Re-merge"}
                    </button>
                    <button className="cp-merged-btn cp-merged-btn--delete" disabled={isDeleting}
                      onClick={() => handleDeleteMerged(m.id, m.chapterName)}>
                      {isDeleting ? "…" : "✕ Delete"}
                    </button>
                  </div>
                </div>
                {hasAudio
                  ? <div className="cp-merged-item__audio">
                    <audio controls src={m.blobUrl} style={{ width: "100%", height: 32 }} />
                  </div>
                  : !isRec && <div className="cp-merged-no-audio">Click ↺ Re-merge to restore audio</div>
                }
              </div>
            );
          })}
        </div>

        {/* RIGHT: Clips + Merge + Stats */}
        <div className="cp-panel">
          <div className="cp-ptitle"><span className="cp-ptitle__dot" />Book Files</div>
          <div className="cp-tree__root">/projects/{project.projectName}/{book.title}/</div>
          <div className="clipsBox">
            {loadingClips
              ? <span className="cp-spinner">Loading clips…</span>
              : clips.length === 0
                ? <div className="cp-empty">No clips yet.</div>
                : Object.entries(groupedClips).map(([chapter, chClips]) => (
                  <div key={chapter}>
                    <div className="cp-tree__ch-header">
                      <div className="cp-tree__ch-label"
                        // onClick={() => setActiveChapter(chapter)}
                        onClick={() => setActiveChapter(activeChapter === chapter ? "" : chapter)}
                      >
                        <span className=""
                        // onClick={()=> setActiveChapter(activeChapter === chapter ? "" : chapter) } 
                        >{activeChapter === chapter ? "▾" : "▸"}</span>
                        {getChapterSerial(chapterOrder, chapter)}. {chapter} <span style={{ color: "var(--text-d)", fontWeight: 400 }}>({chClips.length})</span>
                      </div>


                      <div className="clips-actions">

                        <span className="" style={{ cursor: "pointer" }} title="Add Clip" onClick={() => {
                          // console.log("check add clip function work");



                          setActiveChapter(chapter)
                          // selectedNeedAudio = 0;
                          setNarrationText("");
                          setLoadedClipId(null);
                          setGenerateError("");
                          setClips((prev) =>
                            prev?.map((clip) => ({
                              ...clip,
                              selected: false
                            }))
                          )
                          selectedCount = clips.filter(c => c.selected).length ?? 0;


                        }
                        }
                        >+</span>
                        <button className="cp-tree__ch-selall" onClick={() => toggleAllInChapter(chapter)}>
                          {chClips.every(c => c.selected) ? "Deselect all" : "Select all"}
                        </button>
                      </div>
                    </div>
                    {activeChapter === chapter && (


                      chClips.map(clip => (
                        <div key={clip.id} className="cp-tree__clip " onClick={() => toggleClip(clip.id)}>
                          {(() => {
                            const isDeleting = !!deletingClip[clip.id];
                            console.log("show clicp---->",clip);
                            
                            return (
                              <>
                                <input type="checkbox" checked={clip.selected}
                                  onChange={() => toggleClip(clip.id)} onClick={e => e.stopPropagation()}
                                />
                                {renamingClipId === clip.id ? (
                                  <input
                                    className="cp-clip-rename"
                                    value={renameDraft}
                                    autoFocus
                                    maxLength={120}
                                    onChange={e => setRenameDraft(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    onBlur={() => commitRenameClip(clip)}
                                    onKeyDown={e => {
                                      if (e.key === "Enter") { e.preventDefault(); commitRenameClip(clip); }
                                      if (e.key === "Escape") { e.preventDefault(); cancelRenameClip(); }
                                    }}
                                  />
                                ) : (
                                  <>
                                    <span className="cp-tree__clip-name">{clip.name}</span>
                                    <button
                                      className="cp-clip-load cp-clip-edit"
                                      disabled={savingRename}
                                      onClick={e => { e.stopPropagation(); startRenameClip(clip); }}
                                      title={`Rename ${clip.name}`}
                                      aria-label={`Rename ${clip.name}`}
                                    >
                                      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                        <path
                                          d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    </button>
                                  </>
                                )}
                                {clip.blobUrl
                                  ? (
                                    <button
                                      className={`cp-clip-play ${playingClip === clip.id ? "cp-clip-play--playing" : ""}`}
                                      onClick={e => { e.stopPropagation(); handlePlayClip(clip.id, clip.blobUrl, clip.chapter, clip.name); }}
                                      title={playingClip === clip.id ? "Pause" : "Play"}
                                    >
                                      {playingClip === clip.id ? "⏸" : "▶"}
                                    </button>
                                  )
                                  : (
                                    <button
                                      className="cp-clip-load"
                                      disabled={!!loadingAudio[clip.id]}
                                      onClick={e => { e.stopPropagation(); loadClipAudio(clip.id); }}
                                    >
                                      {loadingAudio[clip.id] ? <span className="cp-spinner" /> : "⬇"}
                                    </button>
                                  )
                                }
                                {clip.selected && (
                                  <button
                                    className={`cp-clip-load ${isLoadedClipText && loadedClipId === clip.id ? "cp-clip-play--playing" : ""}`}
                                    title={
                                      !clip.scriptText?.trim()
                                        ? "No saved text"
                                        : isLoadedClipText && loadedClipId === clip.id
                                          ? `Viewing text from ${clip.name}`
                                          : "Preview saved text in input"
                                    }
                                    disabled={!clip.scriptText?.trim()}
                                    aria-pressed={isLoadedClipText && loadedClipId === clip.id}
                                    style={isLoadedClipText && loadedClipId === clip.id ? { boxShadow: "0 0 0 1px var(--accent) inset" } : undefined}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handlePreviewClipText(clip);
                                    }}
                                  >
                                    View
                                  </button>
                                )}
                                <span className={`cp-clip-badge ${clip.savedToDb ? "cp-clip-badge--db" : "cp-clip-badge--local"}`}>
                                  {clip.savedToDb ? "DB" : "local"}
                                </span>
                                <button
                                  className="cp-clip-load cp-clip-delete"
                                  disabled={isDeleting}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClip(clip);
                                  }}
                                  title={`Delete ${clip.name}`}
                                  aria-label={`Delete ${clip.name}`}
                                >
                                  {isDeleting ? (
                                    <span className="cp-spinner" />
                                  ) : (
                                    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                      <path
                                        d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </>
                            );
                          })()}
                        </div>
                      ))
                    )}
                  </div>
                ))
            }
          </div>
          <div className="cp-divider" />
          <div className="cp-ptitle" style={{ marginBottom: 6 }}><span className="cp-ptitle__dot" />Merge Selected</div>
          <div className="cp-merge-hint">Select clips → merge → save to DB</div>

          {selectedNeedAudio > 0 && (
            <button className="cp-btn cp-btn--load-audio" onClick={loadAudioForSelected} style={{ marginBottom: 8 }}>
              ⬇ Load Audio for {selectedNeedAudio} Clip{selectedNeedAudio > 1 ? "s" : ""}
            </button>
          )}
          {/* <button className="cp-btn cp-btn--merge" onClick={handleMerge} disabled={!selectedCount || !isMerging}> */}
          <button className="cp-btn cp-btn--merge" onClick={handleMerge} disabled={!selectedCount || isMerging || selectedNeedAudio > 0}>
            {isMerging ? <span className="cp-spinner">Merging…</span> : `Merge ${selectedCount > 0 ? `(${selectedCount})` : ""} Selected`}
          </button>

          {mergeMsg && (
            <div className={`cp-merge-msg ${mergeMsg.startsWith("⚠") ? "cp-merge-msg--warn" : ""}`}>{mergeMsg}</div>
          )}

          <div className="cp-divider" />
          <div className="cp-stats">
            {([
              ["Project", project.projectName],
              ["Book", book.title],
              ["Chapters", chapterOrder.length],
              ["Total clips", clips.length],
              // ["Saved to DB", clips.filter(c => c.savedToDb).length],
              ["Audio loaded", clips.filter(c => !!c.base64).length],
              ["Merged", mergedList.length],
              ["Selected", selectedCount],
              ["Total chars", totalChars.toLocaleString()],
              ["Voice", bookCfg.realVoice ? bookCfg.realVoice.split("-").slice(-2).join("-") : "—"],
              ["Language", bookCfg.language],
            ] as [string, string | number][]).map(([k, v]) => (
              <div className="cp-stat" key={k}>
                <span className="cp-stat__k">{k}</span>
                <span className="cp-stat__v">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookEditor;